import {
  OnPollChangedSubscription,
  OnPollChangedSubscriptionVariables,
  OnPollDeletedSubscription,
  OnPollDeletedSubscriptionVariables,
} from "graphql/generated";
import type {
  GetEventPollsQuery,
  GetEventPollsQueryVariables,
} from "graphql/generated";
import { addErrorNotification } from "modules/notification/redux/notificationSlice";
import {
  GET_POLLS,
  ON_POLL_DELETED,
  POLL_SUBSCRIPTION,
} from "modules/polls/graphql";
import { useI18n } from "i18n";
import { useEffect } from "react";
import { useQuery } from "@apollo/client";
import logger from "logging/logger";
import { Errors, trackError } from "modules/monitoring";
import { useAppDispatch } from "store/hooks";
import { updateEventPoll } from "../../graphql/cacheOperations";

export const usePollsSubscription = ({
  eventId,
}: {
  eventId: string | null;
}) => {
  const dispatch = useAppDispatch();
  const { t } = useI18n(["event"]);
  const { subscribeToMore, refetch } = useQuery<
    GetEventPollsQuery,
    GetEventPollsQueryVariables
  >(GET_POLLS);

  useEffect(() => {
    if (!eventId) {
      return undefined;
    }

    refetch({ eventId });

    const unsubscribe = subscribeToMore<
      OnPollChangedSubscription,
      OnPollChangedSubscriptionVariables
    >({
      document: POLL_SUBSCRIPTION,
      variables: { eventId },
      onError: (error) => {
        trackError(Errors.POLLS_SUBSCRIPTION_ERROR, {
          label: Errors.POLLS_SUBSCRIPTION_ERROR,
          error,
        });
        dispatch(
          addErrorNotification({
            message: t("polls.error.subscribe"),
          }),
        );
      },
      updateQuery: updateEventPoll,
    });

    const unsubscribeToDelete = subscribeToMore<
      OnPollDeletedSubscription,
      OnPollDeletedSubscriptionVariables
    >({
      document: ON_POLL_DELETED,
      variables: { eventId },
      onError: (err) => {
        logger.log(JSON.stringify(err));
        dispatch(
          addErrorNotification({
            message: t("polls.error.subscribe"),
          }),
        );
      },
      updateQuery: updateEventPoll,
    });

    return () => {
      unsubscribe();
      unsubscribeToDelete();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId, subscribeToMore, refetch]);
};
